import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicShell from "../components/PublicShell";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { currencySymbol } from "../lib/money";
import {
  Check, Sparkles, Globe, IndianRupee, ShieldCheck, AlertTriangle, Mail, MapPin,
} from "lucide-react";

/**
 * Sprint 4-Final-Geo — Pricing page with STRICT geographic enforcement.
 *
 * Flow:
 *   1. Call /api/payments/pricing-geo on mount — backend reads client IP via
 *      ipapi.co (cached 1h) and decides {region, allowed_currencies, primary_currency,
 *      billing_entity, gateway, manual_review}.
 *   2. India IP → ONLY INR plan cards visible. The currency tab strip shows
 *      "India · INR" only.
 *   3. Foreign IP → INR HIDDEN; only foreign tabs (USD/AED/EUR/GBP/HKD) visible,
 *      auto-defaulted to the regional currency.
 *   4. VPN / proxy / datacenter → manual_review banner with contact CTA;
 *      no plan cards rendered.
 *   5. Provider failure / dev IP → open catalogue (all currencies) so the
 *      product never traps real users behind a broken third-party service.
 *
 * NOTE: client-side filtering is purely cosmetic; /api/payments/checkout/start
 * re-runs the geo gate server-side and refuses cross-region attempts.
 */
const INTL_CURRENCIES = ["USD", "AED", "EUR", "GBP", "HKD"];
const CCY_PREF_KEY = "diplomexa_pricing_currency";

export default function PricingPageV2() {
  const navigate = useNavigate();
  const [geo, setGeo] = useState(null);
  const [data, setData] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [loading, setLoading] = useState(true);
  // null = user hasn't overridden yet (auto-mode); string = user picked manually
  const [userOverride, setUserOverride] = useState(null);

  // Load geo-filtered pricing first
  useEffect(() => {
    api.get("/payments/pricing-geo")
      .then((r) => {
        setGeo(r.data.geo);
        if (r.data.manual_review) {
          setData({ manual_review: true, professional_message: r.data.professional_message });
          setCurrency(null);
          return;
        }
        // Prefer a previously remembered user choice IFF it's still allowed in this region.
        const allowed = r.data.geo?.allowed_currencies
          || (r.data.geo?.region === "india" ? ["INR"] : INTL_CURRENCIES);
        let savedPref = null;
        try {
          savedPref = window.localStorage.getItem(CCY_PREF_KEY);
        } catch { /* SSR / disabled storage */ }
        const detected = r.data.currency || r.data.primary_currency || "INR";
        if (savedPref && allowed.includes(savedPref)) {
          setCurrency(savedPref);
          setUserOverride(savedPref);
        } else {
          setCurrency(detected);
          setUserOverride(null);
        }
        setData(r.data);
      })
      .catch(() => {
        // Fallback: open catalogue if the geo endpoint itself failed
        setData({ plans: [], currencies: [] });
      })
      .finally(() => setLoading(false));
  }, []);

  // When user manually picks a currency, remember it in localStorage so the
  // choice survives refresh/return visits. When user resets to "auto",
  // we wipe the key so the next session uses detected geo currency again.
  const chooseCurrency = (c, persist = true) => {
    setCurrency(c);
    setUserOverride(persist ? c : null);
    try {
      if (persist) window.localStorage.setItem(CCY_PREF_KEY, c);
      else window.localStorage.removeItem(CCY_PREF_KEY);
    } catch { /* noop */ }
  };

  const resetToLocal = () => {
    const detected = geo?.primary_currency || data?.primary_currency || "INR";
    chooseCurrency(detected, false);
  };

  // Re-fetch plans when user switches currency (within allowed set)
  useEffect(() => {
    if (!currency || data?.manual_review) return;
    api.get(`/payments/pricing-geo?currency=${currency}`)
      .then((r) => setData((d) => ({ ...d, ...r.data })))
      .catch(() => { /* keep current */ });
  }, [currency, data?.manual_review]);

  const allowedCurrencies = useMemo(() => {
    if (!geo) return ["INR", ...INTL_CURRENCIES];
    if (geo.region === "india") return ["INR"];
    if (geo.region === "international") return INTL_CURRENCIES;
    if (geo.region === "manual_review") return [];
    return ["INR", ...INTL_CURRENCIES]; // dev / unknown / fallback
  }, [geo]);

  const showInrTab = allowedCurrencies.includes("INR");
  const intlTabs = allowedCurrencies.filter((c) => c !== "INR");

  const isInr = currency === "INR";
  const entity = isInr
    ? { name: "Factskoot Eduwellness Private Limited", city: "Bengaluru, India",
        gateway: "Razorpay", tax: "18% GST" }
    : { name: "Destineze Educational Voyages FZCO", city: "IFZA Dubai, UAE",
        gateway: "Stripe", tax: "UAE VAT not charged currently" };

  return (
    <PublicShell>
      <section className="relative bg-brand-navy-dark text-brand-champagne overflow-hidden">
        <div aria-hidden className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
        <div aria-hidden className="absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-brand-gold/10 blur-3xl" />
        <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <p className="eyebrow text-brand-gold flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Diplomexa Pricing
          </p>
          <h1 className="mt-4 headline-luxury text-4xl sm:text-5xl lg:text-[4.5rem] text-brand-champagne">
            One platform.
            <span className="block italic text-brand-gold font-display font-medium">
              Two billing geographies.
            </span>
          </h1>
          <div className="divider-gold-hairline mt-8 max-w-md" />
          <p className="mt-8 text-base text-brand-champagne/70 max-w-2xl leading-relaxed">
            Built for global delivery — billed locally where it matters. Indian
            customers are invoiced by <span className="text-brand-gold font-semibold">Factskoot Eduwellness Pvt Ltd</span> with GST;
            international customers by <span className="text-brand-gold font-semibold">Destineze Educational Voyages FZCO</span> from
            our Dubai entity.
          </p>

          {/* Geo banner — explains why only one set of currencies is visible */}
          {geo && (geo.region === "india" || geo.region === "international") && (
            <div
              className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-xs"
              data-testid="geo-region-banner"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" />
              <span>
                {geo.region === "india" ? (
                  <>Detected billing region: <strong>India</strong> — INR pricing shown,
                  invoiced by Factskoot.</>
                ) : (
                  <>Detected billing region: <strong>{geo.country_name || geo.country_code}</strong> —
                  international pricing shown, invoiced by Destineze.</>
                )}
              </span>
            </div>
          )}

          {/* Currency toggle — filtered by allowed_currencies */}
          {!data?.manual_review && allowedCurrencies.length > 0 && (
            <div className="mt-8 inline-flex rounded-full bg-white/10 border border-white/15 p-1" data-testid="pricing-currency-toggle">
              {showInrTab && (
                <button
                  type="button"
                  onClick={() => chooseCurrency("INR")}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
                    currency === "INR" ? "bg-[#D4AF37] text-[#0A192F]" : "text-slate-300"
                  }`}
                  data-testid="pricing-currency-INR"
                >
                  <IndianRupee className="h-3 w-3" /> India · INR
                </button>
              )}
              {intlTabs.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => chooseCurrency(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
                    currency === c ? "bg-[#D4AF37] text-[#0A192F]" : "text-slate-300"
                  }`}
                  data-testid={`pricing-currency-${c}`}
                >
                  <Globe className="h-3 w-3" /> {c}
                </button>
              ))}
            </div>
          )}

          {/* "Pay in your local currency" auto-detect pill — shows the
              detected regional currency and lets the user reset to it.
              Hidden in India region (single-currency, no toggle value). */}
          {geo?.primary_currency && currency && geo?.region !== "india" && allowedCurrencies.includes(geo.primary_currency) && (
            <div className="mt-3" data-testid="pricing-local-currency-wrap">
              {userOverride && userOverride !== geo.primary_currency ? (
                <button
                  type="button"
                  onClick={resetToLocal}
                  data-testid="pricing-local-currency-reset"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25 text-[11px] font-semibold transition-colors"
                  title={`Detected location: ${geo.country_name || geo.country || geo.region}`}
                >
                  <MapPin className="h-3 w-3" />
                  Pay in your local currency · {currencySymbol(geo.primary_currency)} {geo.primary_currency}
                  <span className="opacity-70">(reset)</span>
                </button>
              ) : (
                <span
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-400 text-[11px]"
                  data-testid="pricing-local-currency-active"
                  title={`Detected location: ${geo.country_name || geo.country || geo.region}`}
                >
                  <MapPin className="h-3 w-3 text-emerald-300" />
                  Showing local currency for {geo.country_name || geo.country || "your region"}
                </span>
              )}
            </div>
          )}

          {/* Billing entity banner */}
          {currency && !data?.manual_review && (
            <div
              className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-[#D4AF37]/30 text-xs"
              data-testid="pricing-billing-entity-banner"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" />
              <span>
                Billed by <span className="text-[#D4AF37] font-semibold">{entity.name}</span>
                {" · "}{entity.city} · via {entity.gateway} · {entity.tax}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Manual-review banner (VPN / proxy / datacenter) */}
      {data?.manual_review && (
        <section className="bg-amber-50 border-y border-amber-200" data-testid="geo-manual-review-banner">
          <div className="max-w-3xl mx-auto px-6 py-12 text-center">
            <AlertTriangle className="h-8 w-8 mx-auto text-amber-700" />
            <h2 className="mt-3 text-xl font-bold text-amber-900">
              Billing region needs verification
            </h2>
            <p className="mt-2 text-sm text-amber-900 max-w-xl mx-auto">
              {data.professional_message}
            </p>
            <div className="mt-5 flex items-center justify-center gap-3">
              <Link
                to="/contact"
                className="px-4 py-2 rounded-md bg-[#0A192F] text-white text-sm font-semibold inline-flex items-center gap-1.5"
                data-testid="manual-review-contact-cta"
              >
                <Mail className="h-3.5 w-3.5" /> Talk to billing
              </Link>
              <Link to="/billing-notice" className="text-xs text-[#0F4C81] underline">
                Read Billing Entity Notice
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Plans grid */}
      {!data?.manual_review && (
        <section className="bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24" data-testid="pricing-plans-grid">
            {loading && (
              <p className="text-center text-slate-500 text-sm">Loading regional pricing…</p>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(data?.plans || []).map((p) => (
                <PlanCard
                  key={p.plan_id}
                  plan={p}
                  currency={currency}
                  onChoose={() => {
                    if (p.is_free) navigate(`/register?plan=${p.plan_id}`);
                    else if (p.is_enterprise) navigate("/contact?topic=enterprise");
                    else navigate(`/checkout?plan=${p.plan_id}&currency=${currency}`);
                  }}
                />
              ))}
            </div>
            <div className="mt-10 text-center">
              <p className="text-xs text-slate-500">
                Need help choosing?{" "}
                <Link to="/contact" className="text-[#0F4C81] font-semibold underline">
                  Talk to us
                </Link>
                {" · "}
                <Link to="/refund-policy" className="text-[#0F4C81] font-semibold underline">
                  Refund Policy
                </Link>
                {" · "}
                <Link to="/billing-notice" className="text-[#0F4C81] font-semibold underline">
                  Billing Notice
                </Link>
              </p>
            </div>
          </div>
        </section>
      )}
    </PublicShell>
  );
}

function PlanCard({ plan, currency, onChoose }) {
  const fmt = (a) => {
    if (a === null || a === undefined) return "—";
    if (a === 0) return "Free";
    return `${plan.symbol}${Number(a).toLocaleString()}`;
  };
  const period = plan.billing_period === "annual" ? "/year"
              : plan.billing_period === "one_time" ? "/event"
              : "";
  return (
    <div
      className={`rounded-2xl border bg-white p-6 flex flex-col ${
        plan.tier === "organiser" ? "border-[#D4AF37] shadow-lg shadow-[#D4AF37]/20 scale-[1.02]" : "border-slate-200"
      }`}
      data-testid={`pricing-plan-${plan.plan_id}`}
    >
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold">
        {plan.tier === "organiser" && "Most popular · "}
        {plan.tier}
      </p>
      <h3 className="mt-2 text-xl font-semibold text-[#0A192F]">{plan.name}</h3>
      <p className="mt-3 text-3xl font-bold text-[#0A192F]">
        {plan.is_enterprise ? "Contact sales" : fmt(plan.amount)}
        {!plan.is_enterprise && plan.amount > 0 && (
          <span className="text-xs text-slate-500 font-normal">{period}</span>
        )}
      </p>
      <ul className="mt-4 space-y-1.5 flex-1">
        {(plan.features || []).map((f) => (
          <li key={f} className="text-sm text-slate-700 flex items-start gap-2">
            <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onChoose}
        className={`mt-5 ${plan.tier === "organiser" ? "bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F]" : ""}`}
        data-testid={`pricing-plan-${plan.plan_id}-cta`}
      >
        {plan.is_free ? "Start free" : plan.is_enterprise ? "Contact us" : "Choose this plan"}
      </Button>
      {currency === "INR" && (
        <p className="mt-3 text-[10px] uppercase tracking-wider text-slate-500">
          + 18% GST · Billed by Factskoot
        </p>
      )}
      {currency !== "INR" && !plan.is_free && (
        <p className="mt-3 text-[10px] uppercase tracking-wider text-slate-500">
          UAE VAT not charged · Billed by Destineze
        </p>
      )}
    </div>
  );
}
